/**
 * SofFolderPicker — "Add from folder" for the Source of Funds evidence table.
 *
 * Emily, 26 Oct 2026: the client's statements and payslips are already in
 * the matter's compliance folder, yet each evidence row made her open the
 * document in SharePoint, copy its link and paste it back. This lists the
 * matter folder (same read-only route as the Documents card, BM-H4266),
 * opens the compliance folder and any SoF folder inside it, and lets her
 * tick the documents. Each ticked file comes back as { id, name, web_url } and
 * becomes an evidence row with its link filled in.
 *
 * Nothing here writes to SharePoint.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { Folder, FileText, Loader2, ChevronRight, Check } from "lucide-react";

// Folders opened for her on the way in, in order: the compliance folder,
// then a source of funds folder inside it if there is one.
const AUTO_OPEN = [/compliance/i, /source of funds|\bsof\b|\bsow\b/i];

export default function SofFolderPicker({ matterRef, existingUrls = [], onAdd, onClose }) {
  const enc = encodeURIComponent(matterRef || "");
  const [trail, setTrail] = useState([]);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Ticked files across every folder visited, keyed by id.
  const [ticked, setTicked] = useState({});
  const autoStep = useRef(0);

  const folderId = trail.length ? trail[trail.length - 1].id : null;

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/documents/matter/${enc}`, {
        params: folderId ? { folder_id: folderId } : {},
      });
      const data = res.data || {};
      setListing(data);
      const base = folderId ? trail : (data.matter_folder
        ? [{ id: data.matter_folder.id, name: data.matter_folder.name }] : []);
      if (!folderId) setTrail(base);
      if (autoStep.current < AUTO_OPEN.length) {
        const match = (data.folders || []).find((f) => AUTO_OPEN[autoStep.current].test(f.name));
        autoStep.current = match ? autoStep.current + 1 : AUTO_OPEN.length;
        if (match) setTrail([...base, { id: match.id, name: match.name }]);
      }
    } catch (e) {
      setListing(null);
      setErr(errorMessage(e, "Could not read the matter's SharePoint folder."));
    } finally {
      setLoading(false);
    }
    // trail is read only to extend it; folderId drives the reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enc, folderId]);

  useEffect(() => { load(); }, [load]);

  const files = listing?.files || [];
  const folders = listing?.folders || [];
  const already = new Set(existingUrls.filter(Boolean));
  const tickedList = Object.values(ticked);

  const toggle = (f) => setTicked((t) => {
    const next = { ...t };
    if (next[f.id]) delete next[f.id];
    else next[f.id] = { id: f.id, name: f.name, web_url: f.web_url };
    return next;
  });

  const openFolder = (f) => {
    autoStep.current = AUTO_OPEN.length;
    setTrail((t) => [...t, { id: f.id, name: f.name }]);
  };
  const goTo = (i) => {
    autoStep.current = AUTO_OPEN.length;
    setTrail((t) => t.slice(0, i + 1));
  };

  return (
    <div className="mb-3 border border-white/10 rounded-md p-3 bg-white/[0.02]" data-testid="sof-folder-picker">
      <div className="flex items-center gap-1 text-xs text-slate-400 mb-2 flex-wrap" data-testid="sof-folder-picker-trail">
        {trail.map((t, i) => (
          <span key={t.id} className="inline-flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3 h-3" />}
            <button type="button" onClick={() => goTo(i)} className="hover:text-white truncate max-w-[16rem]">
              {i === 0 ? "Matter folder" : t.name}
            </button>
          </span>
        ))}
      </div>

      {loading && (
        <div className="flex items-center text-xs text-slate-400 py-3">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Reading the SharePoint folder…
        </div>
      )}
      {!loading && err && <p className="text-xs text-status-red py-2" data-testid="sof-folder-picker-error">{err}</p>}
      {!loading && !err && folders.length === 0 && files.length === 0 && (
        <p className="text-xs text-slate-400 py-2">This folder is empty.</p>
      )}

      {!loading && !err && (
        <ul className="max-h-64 overflow-y-auto text-xs divide-y divide-white/5">
          {folders.map((f) => (
            <li key={f.id}>
              <button type="button" onClick={() => openFolder(f)}
                      data-testid="sof-folder-picker-folder"
                      className="w-full flex items-center gap-2 py-1.5 text-left text-slate-200 hover:bg-white/5">
                <Folder className="w-4 h-4 text-teal shrink-0" /> <span className="truncate">{f.name}</span>
              </button>
            </li>
          ))}
          {files.map((f) => {
            const onFile = already.has(f.web_url);
            return (
              <li key={f.id}>
                <label className={`flex items-center gap-2 py-1.5 ${onFile ? "text-slate-500" : "text-slate-200 cursor-pointer hover:bg-white/5"}`}>
                  <input type="checkbox" disabled={onFile || !f.web_url}
                         checked={!!ticked[f.id]} onChange={() => toggle(f)}
                         data-testid={`sof-folder-picker-file-${f.id}`} />
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="truncate" title={f.name}>{f.name}</span>
                  {onFile && <span className="ml-auto inline-flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> already a row</span>}
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center justify-end gap-2 mt-3">
        <button type="button" onClick={onClose}
                className="text-xs px-2 py-1 rounded-md text-slate-300 hover:bg-white/5"
                data-testid="sof-folder-picker-cancel">
          Cancel
        </button>
        <button type="button" disabled={!tickedList.length}
                onClick={() => onAdd(tickedList)}
                className="text-xs px-2 py-1 rounded-md border border-white/10 text-white bg-indigo-600/70 hover:bg-indigo-600 disabled:opacity-40"
                data-testid="sof-folder-picker-add">
          Add {tickedList.length || ""} {tickedList.length === 1 ? "document" : "documents"}
        </button>
      </div>
    </div>
  );
}
